import React, { createContext, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Creators as StockItemActions } from "../../store/ducks/stockItem";
import { findAll as findAllUnits } from "../../services/unitService";
import { getAllSuppliers } from "../../services/supplierService";
import { findAll as findAllGroupItems } from "../../services/groupItemService";
import { findAll as findAllItemsGroups } from "../../services/itemsGroupService";
import { findAll as findAllVeterinarySupplements } from "../../services/veterinaryService";
import { findAll as findAllUnities } from "../../services/unitService";

export const StockItemContext = createContext({});

export const StockItemContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  const { groupSelected, farmSelected } = useSelector((state) => state.app);
  const stockItemState = useSelector((state) => state.stockItem);

  const [supplierSearchText, setSupplierSearchText] = useState("");
  const [textSearchSupplement, setTextSearchSupplement] = useState("");

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [units, setUnits] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [groupItems, setGroupItems] = useState([]);
  const [isLoadingGroupItems, setIsLoadingGroupItems] = useState(false);
  const [itemsGroups, setItemsGroups] = useState([]);
  const [isLoadingItemsGroups, setIsLoadingItemsGroups] = useState(false);
  const [veterinarySupplements, setVeterinarySupplements] = useState([]);

  const groupId = groupSelected?.id;
  const farmId = farmSelected?.id;

  useEffect(() => {
    if (groupId && farmId) {
      dispatch(StockItemActions.indexStockItem(groupId, farmId));
    }
  }, [groupId, farmId, dispatch]);

  const loadVeterinarySupplements = useCallback(async () => {
    try {
      const [
        {
          data: { results },
        },
        {
          data: { results: unities },
        },
      ] = await Promise.all([
        findAllVeterinarySupplements({
          groupId,
          farmId,
        }),
        findAllUnities({
          groupId,
          farmId,
        }),
      ]);

      const veterinarySupplementsActive = results
        .filter(
          (veterinarySupplement) =>
            veterinarySupplement.status === "A" ||
            veterinarySupplement.status === "Active",
        )
        .filter(
          (veterinarySupplement) => veterinarySupplement.stockItemId === null,
        )
        .map((vetSup) => {
          const unitIndex = unities.findIndex(
            (unit) => unit.id === vetSup.unitId,
          );
          vetSup.unitName = unitIndex >= 0 ? unities[unitIndex].name : null;
          return vetSup;
        });

      setVeterinarySupplements(veterinarySupplementsActive);
    } catch (error) {}
  }, [groupId, farmId]);

  const loadUnits = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingUnits(true);
    try {
      const {
        data: { results },
      } = await findAllUnits({ groupId, farmId });
      setUnits(results || []);
    } catch {
      setUnits([]);
    } finally {
      setIsLoadingUnits(false);
    }
  }, [groupId, farmId]);

  const loadSuppliers = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingSuppliers(true);
    try {
      const {
        data: { results },
      } = await getAllSuppliers({
        groupId,
        farmId,
        withoutPagination: true,
      });
      setSuppliers(results || []);
    } catch {
      setSuppliers([]);
    } finally {
      setIsLoadingSuppliers(false);
    }
  }, [groupId, farmId]);

  const loadGroupItems = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingGroupItems(true);
    try {
      const {
        data: { results },
      } = await findAllGroupItems({ groupId, farmId, size: 1000 });
      setGroupItems(
        Array.isArray(results) ? results : (results?.content ?? []),
      );
    } catch {
      setGroupItems([]);
    } finally {
      setIsLoadingGroupItems(false);
    }
  }, [groupId, farmId]);

  const loadItemsGroups = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingItemsGroups(true);
    try {
      const {
        data: { results },
      } = await findAllItemsGroups({ groupId, farmId, size: 1000 });
      setItemsGroups(
        Array.isArray(results) ? results : (results?.content ?? []),
      );
    } catch {
      setItemsGroups([]);
    } finally {
      setIsLoadingItemsGroups(false);
    }
  }, [groupId, farmId]);

  const handleRefresh = () => {
    dispatch(StockItemActions.indexStockItem(groupId, farmId));
  };

  const handleTabChange = (tabIndex) => setActiveTab(tabIndex);

  const handleSearch = (term) => setSearchTerm(term);

  const handleToggleInactive = () => setShowInactive((prev) => !prev);

  const handleOpenDetail = (item) => {
    loadUnits();
    loadSuppliers();
    loadGroupItems();
    loadItemsGroups();
    dispatch(StockItemActions.showStockItem(groupId, farmId, item.id));
    dispatch(StockItemActions.showDrawerStockItem());
  };

  const handleOpenNewItem = () => {
    Promise.all([
      loadUnits(),
      loadSuppliers(),
      loadGroupItems(),
      loadItemsGroups(),
      loadVeterinarySupplements(),
    ]);
    setSupplierSearchText("");
    setTextSearchSupplement("");
    dispatch(StockItemActions.showModalNewStockItem());
  };

  const handleSaveItem = (values, id = null) => {
    dispatch(StockItemActions.saveStockItem(groupId, farmId, id, values));
  };

  const handleDeleteItem = (id) => {
    dispatch(StockItemActions.deleteStockItem(groupId, farmId, id));
  };

  const handleToggleStatus = (id, active) => {
    dispatch(
      StockItemActions.toggleStatusStockItem(groupId, farmId, id, active),
    );
  };

  const handleCloseDrawer = () => {
    dispatch(StockItemActions.hideDrawerStockItem());
  };

  const handleCloseModal = () => {
    setSupplierSearchText("");
    setTextSearchSupplement("");
    dispatch(StockItemActions.hideModalNewStockItem());
  };

  return (
    <StockItemContext.Provider
      value={{
        groupId,
        farmId,
        activeTab,
        searchTerm,
        showInactive,
        units,
        isLoadingUnits,
        suppliers,
        isLoadingSuppliers,
        groupItems,
        isLoadingGroupItems,
        itemsGroups,
        isLoadingItemsGroups,
        ...stockItemState,
        supplierSearchText,
        setSupplierSearchText,
        textSearchSupplement,
        setTextSearchSupplement,
        veterinarySupplements,
        handleRefresh,
        handleTabChange,
        handleSearch,
        handleToggleInactive,
        handleOpenDetail,
        handleOpenNewItem,
        handleSaveItem,
        handleDeleteItem,
        handleToggleStatus,
        handleCloseDrawer,
        handleCloseModal,
        loadUnits,
        loadSuppliers,
        loadGroupItems,
        loadItemsGroups,
        loadVeterinarySupplements,
      }}
    >
      {children}
    </StockItemContext.Provider>
  );
};
